/**
 * @lines98/client/services/meta-prefetch — РАННИЙ прогрев мета-состояния + дедуп
 * in-flight `GET /meta/state`, чтобы первый заход в Мастерскую рисовался ГОТОВЫМ.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-playtest fix 2026-06-10, прецедент `prefetchDailySeed`):
 * `lib/meta-snapshot` убрал передёргивание на ПОВТОРНЫХ заходах (синхронный кэш-снимок),
 * но на ПЕРВОМ (холодный кэш — нет снимка) динамика Мастерской всё ещё ждёт сетевой
 * ответ `getMetaState`, отчего числа «вщёлкиваются». Этот модуль — ЕДИНСТВЕННАЯ точка
 * раннего прогрева: на boot (после готовности идентичности — Bearer нужен, гость → 401)
 * фоном запускаем `getMetaState`, кладём результат в `meta-snapshot`. К моменту, когда
 * игрок откроет Мастерскую, данные обычно уже прилетели → даже первый заход рисуется из
 * свежего снимка.
 *
 * **Дедуп (модуль, НЕ копипаст fetch):** хранит in-flight промис одного `getMetaState`.
 * `getMetaStateShared()` отдаёт ТОТ ЖЕ полёт, если он уже летит (Мастерская при mount
 * зовёт shared-версию вместо прямого `getMetaState` → НЕ делает второй дубль-запрос, а
 * переиспользует префетч). После ресолва промис очищается → следующий вызов (ресинк
 * после мутации) делает свежий запрос (server-truth, не залипает на старом полёте).
 *
 * **Server-truth цел (I-G6 / AC-G11):** это ПРОСТО более ранний `getMetaState` (тот же
 * легальный GET, та же серверная истина) — раньше во времени, не «новый источник».
 * Анти-чит/движок/replayScore не затронуты: префетч ничего не верифицирует и не пишет.
 * Снимок (`meta-snapshot`) — UX-проекция; истина всегда — следующий `getMetaState`.
 *
 * **Гость (401):** префетч тихо НИЧЕГО не кэширует (`saveMetaSnapshot` no-op без
 * playerId-ключа; ошибка проглатывается) — Мастерская покажет «Войди», как раньше.
 *
 * Cross-references:
 *   - services/daily-api.ts `prefetchDailySeed` (прецедент: прогрев на boot, errors swallowed).
 *   - services/meta-api.ts `getMetaState` (тот самый легальный GET — server-truth).
 *   - lib/meta-snapshot.ts (buildMetaSnapshot / saveMetaSnapshot — кэш-проекция).
 *   - screens/workshop-screen.ts (consumer: load() зовёт getMetaStateShared, не дубль).
 */

import { createSharedFlight } from '../lib/shared-flight.js';
import { buildMetaSnapshot, saveMetaSnapshot } from '../lib/meta-snapshot.js';
import { emitMetaStateGranted } from '../lib/meta-state-events.js';
import { emitIdentityChanged } from '../lib/identity-events.js';

import { getMetaState, type MetaStateResult } from './meta-api.js';
import { getCurrent, isAuthed, update as updateIdentity } from './identity.js';

// =============================================================================
// In-flight dedup (один полёт getMetaState — переиспользуется префетчем и mount'ом)
// =============================================================================

/**
 * Дедуп in-flight `getMetaState` поверх общего «ствола» `createSharedFlight` (один полёт
 * на всю мету — ключ-дефолт). Логику in-flight/finally-очистки держит фабрика; здесь —
 * только «что запросить» (`getMetaState`). Тот же движок дедупа переиспользует лидерборд.
 */
const flight = createSharedFlight<MetaStateResult>(() =>
  // helper-unlock A2 (AC-9): на КАЖДЫЙ успешный server-truth меты эмитим granted в общий
  // seam (meta-state-events) — рейл-бейдж «есть новые помощники» обновляется, кто бы ни
  // инициировал запрос (boot-префетч / mount мета-экрана / ресинк). Эмит в ПРОДЮСЕРЕ
  // дедупа (не в getMetaStateShared) → identity промиса сохраняется (оба конкурентных
  // вызова получают ТОТ ЖЕ промис, дедуп не ломается). server-truth цел: эмитим что
  // прислал сервер (`gnomeUnlocksGranted`), клиент не пересчитывает (I-Gn4). Дёшево (число).
  getMetaState().then((res) => {
    if (res.ok) emitMetaStateGranted(res.gnomeUnlocksGranted ?? 0);
    return res;
  }),
);

/**
 * Запустить `getMetaState` ИЛИ переиспользовать уже летящий полёт (дедуп).
 *
 * Если запрос уже в полёте (например, его начал boot-префетч), возвращает ТОТ ЖЕ
 * промис — потребитель (Мастерская при mount) НЕ делает второй дубль-запрос. После
 * ресолва in-flight очищается, поэтому следующий вызов (ресинк после мутации) делает
 * свежий запрос (server-truth — не отдаём устаревший полёт).
 *
 * Идемпотентно от network-perspective (GET, без мутации). Server-truth не меняется —
 * это тот же `getMetaState`, просто без дублирования параллельных вызовов.
 */
export function getMetaStateShared(): Promise<MetaStateResult> {
  return flight.shared();
}

// =============================================================================
// Boot prefetch (fire-and-forget — прогрев снимка к первому заходу)
// =============================================================================

/**
 * Fire-and-forget фоновый прогрев мета-состояния на boot (main.ts, после готовности
 * идентичности). Через `getMetaStateShared` (дедуп) тянет server-truth и при успехе
 * кладёт снимок (`saveMetaSnapshot`) — к моменту входа в Мастерскую снимок свежий, даже
 * на ПЕРВОМ заходе. Если игрок успел открыть Мастерскую до ресолва — её `load()` зовёт
 * тот же `getMetaStateShared`, переиспользуя ЭТОТ полёт (без второго запроса).
 *
 * Best-effort: ошибка/гость (401 / network / 5xx) проглатывается и НИКОГДА не ломает
 * boot — снимок просто остаётся холодным, Мастерская идёт обычным путём (скелет → fade /
 * «Войди»). Server-truth цел — это просто более ранний легальный `getMetaState`.
 *
 * Не блокирует рендер: вызывать без await (как `prefetchDailySeed`).
 */
export function prefetchMetaState(): void {
  void getMetaStateShared()
    .then(async (res) => {
      // Только успех → освежить снимок (saveMetaSnapshot сам no-op для гостя без ключа).
      if (!res.ok) return;
      saveMetaSnapshot(buildMetaSnapshot(res));
      // РЕКОНСИЛЯЦИЯ ИМЕНИ НА РЕЙЛЕ/ВЕЗДЕ (owner-bug 2026-06-21: «при перезаходе за другого
      // игрока ник не подтягивается на рейл»). На КАЖДОМ входе в приложение тянем server-truth
      // display_name из meta-state и кладём в identity; при расхождении эмитим identity-changed
      // → account-slot рейла (и открытый Профиль) пересобираются с верным ником. Покрывает
      // reopen/reload/cross-device (login-modal ставит имя сам из login-ответа). Только для
      // реального аккаунта (isAuthed) — у гостя на рейле пилюля «Войти», ник не показывается.
      //
      // FORWARD-COMPAT (2026-06-22): НЕ затираем известное локальное имя серверным `null`.
      // `res.displayName = body.display_name ?? null` (meta-api): неполный/stale server-ответ
      // без поля `display_name` даёт null = «поле не прислано», а НЕ «имя очищено». Безусловная
      // реконсиляция стёрла бы валидный локальный ник → залогиненный с именем ошибочно падал бы
      // в «придумайте имя» (free) на Профиле. Reconcile ТОЛЬКО на НЕПУСТОЕ серверное имя
      // (исходный owner-bug — server-HAS-name). Зеркалит ту же защиту в profile-screen.load().
      const cur = getCurrent();
      if (cur && isAuthed() && res.displayName != null && (cur.displayName ?? null) !== res.displayName) {
        await updateIdentity({
          playerId: cur.playerId,
          secretToken: cur.secretToken,
          displayName: res.displayName,
        });
        emitIdentityChanged();
      }
    })
    .catch(() => {
      /* best-effort прогрев — offline / ошибка ок, снимок остаётся холодным */
    });
}

/** Test seam: сбросить in-flight дедуп между кейсами (не трогает снимок/сеть). */
export function _resetMetaPrefetchForTests(): void {
  flight.reset();
}
